import {
    EzObject,
    EzString,
    EzHtml
} from '/ezlibrary/helpers/EzHelpers.js';

import {
    // TODO: Remove once all use is migrated to using the official import
    // of import { EzValidationResponseStatus } from '/ezlibrary/enums/Enumerations.js';
    EzValidationResponseStatus
} from '/ezlibrary/enums/EzEnumerations.js';

/**
 * @class
 * @description
 * Represents the response of an validation from ez-validation.js (or other validators)
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 * Import with:
 *      import { EzValidationResponse } from '/ezlibrary/EzValidationResponse.js';
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 */
class EzValidationResponse {
    /**
     * @static
     * @public @readonly @property
     * Gets successfully validated message: 'Validation Successful'
     * @returns {string}
     */
    static get DEFAULT_VALIDATION_SUCCESSFUL_MESSAGE() {
        return 'Validation Successful';
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the default successfully validated HTML message:
     * Template:
     *  EzHtml.build`
     *      <div
     *          id="EzValidationMessage"
     *          style="padding:2px;">
     *          ${EzValidationResponse.DEFAULT_VALIDATION_SUCCESSFUL_MESSAGE}
     *      </div>`
     * @returns {string}
     */
    static get DEFAULT_VALIDATION_SUCCESSFUL_HTML_MESSAGE() {
        return EzValidationResponse.buildValidationHtmlMessage(EzValidationResponse.DEFAULT_VALIDATION_SUCCESSFUL_MESSAGE);
    }

    /**
     * Builds a validation html message with the provided nonHtmlMessage value.
     * If the nonHtmlMessage value is null, undefined, or empty, then the default success mesage is used instead.
     * Template:
     *  EzHtml.build`
     *      <div
     *          id="EzValidationMessage"
     *          style="padding:2px;">${EzString.stringWithLengthOrDefault(nonHtmlMessage, EzValidationResponse.DEFAULT_VALIDATION_SUCCESSFUL_MESSAGE)}
     *      </div>'
     * @param {undefined|null|string} nonHtmlMessage
     * @returns {string}
     */
    static buildValidationHtmlMessage(nonHtmlMessage) {
        return EzHtml.build`
            <div
                id="EzValidationMessage"
                style="padding:2px;">
                ${EzString.stringWithTextOrDefault(nonHtmlMessage, EzValidationResponse.DEFAULT_VALIDATION_SUCCESSFUL_MESSAGE)}
            </div>`;
    }

    /**
     * @public @constructor
     * Constructor for EzValidationResponse
     * @param {undefined|null|string} ezValidationResponseStatus
     * Expecting a valid enum property value from EzValidationResponseStatus
     * Default: EzValidationResponseStatus.VALID
     * @param {undefined|null|string} message
     * Default: EzValidationResponse.DEFAULT_VALIDATION_SUCCESSFUL_MESSAGE
     * @param {undefined|null|string} htmlMessage
     * Default when undefined or null:
     *  EzValidationResponse.buildValidationHtmlMessage(
     *      EzString.stringWithTextOrDefault(
     *          message,
     *          EzValidationResponse.DEFAULT_VALIDATION_SUCCESSFUL_MESSAGE))
     * @param {undefined|null|number|string} errorCode
     * Default: EzValidationResponseStatus.toErrorCode(EzValidationResponseStatus.VALID)
     * @param {undefined|null|string} focusElementId}
     */
    constructor(
        ezValidationResponseStatus = EzValidationResponseStatus.VALID,
        message = EzValidationResponse.DEFAULT_VALIDATION_SUCCESSFUL_MESSAGE,
        htmlMessage = EzValidationResponse.DEFAULT_VALIDATION_SUCCESSFUL_MESSAGE,
        errorCode = 0,
        focusElementId = null) {

        this.ezStatus = ezValidationResponseStatus;

        this.message = message;

        this.htmlMessage = htmlMessage;

        this.errorCode = errorCode;

        this.focusElementId = focusElementId;
    }

    /**
     * @private @field
     * Stores the EzValidationResponseStatus
     * @type {EzValidationResponseStatus}
     */
    #ezStatus = EzValidationResponseStatus.VALID;
    /**
     * @public @property @getter
     * Gets the EzValidationResponseStatus
     * @returns {EzValidationResponseStatus}
     */
    get ezStatus() {
        return this.#ezStatus;
    }
    /**
     * @public @property @setter
     * Sets the EzValidationResponseStatus
     * @param {EzValidationResponseStatus} ezValidationResponseStatus
     */
    set ezStatus(ezValidationResponseStatus) {
        this.#ezStatus = EzString.hasLength(ezValidationResponseStatus)
            ? EzValidationResponseStatus.ezAsEnum(ezValidationResponseStatus)
            : EzValidationResponseStatus.VALID;

        if (EzValidationResponseStatus.UNKNOWN === this.ezStatus) {
            this.#ezStatus = EzValidationResponseStatus.VALID;
        }
    }

    /**
     * @private @field
     * Stores the error code number
     * @type {number|string}
     */
    #errorCode = 0;
    /**
     * @public @property @getter
     * Returns the error code number
     * @returns {number|string}
     */
    get errorCode() {
        return this.#errorCode;
    }
    /**
     * @public @property @setter
     * Sets the error code number
        If the errorCode is undefined or null, then zero is assigned.
     * @param {number|string} errorCode
     */
    set errorCode(errorCode) {
        this.#errorCode = EzObject.assignOrDefault(
            errorCode,
            EzValidationResponseStatus.ezToErrorCode(this.ezStatus));
    }

    /**
     * @private @field
     * Stores the error message
     * @type {string}
     */
    #message = EzString.EMPTY;
    /**
     * @public @property @getter
     * Gets the error message
     * @returns {string}
     */
    get message() {
        return this.#message;
    }
    /**
     * @public @property @setter
     * Sets the error message
     * @param {undefined|null|string} message
     */
    set message(message) {
        this.#message = EzString.stringWithTextOrDefault(
            message,
            EzValidationResponse.DEFAULT_VALIDATION_SUCCESSFUL_MESSAGE);
    }

    /**
     * @private @field
     * Stores the error message as HTML
     * @type {string}
     */
    #htmlMessage = EzString.EMPTY;
    /**
     * @public @property @getter
     * Gets the error message as HTML
     * @returns {string}
     */
    get htmlMessage() {
        return this.#htmlMessage;
    }
    /**
     * @public @property @setter
     * Sets the error message as HTML
     * @param {string} ezValidationResponseStatus
     */
    set htmlMessage(htmlMessage) {
        this.#htmlMessage = EzString.stringWithTextOrDefault(
            htmlMessage,
            EzValidationResponse.buildValidationHtmlMessage(this.message));
    }

    /**
     * @private @field
     * Stores the element id that should receive focus when
     * validation fails.
     * @type {string}
     */
    #focusElementId = null;
    /**
     * @public @property @getter
     * Gets the error message as HTML
     * @returns {string}
     */
    get focusElementId() {
        return this.#focusElementId;
    }
    /**
     * @public @property @setter
     * Sets the error message as HTML
     * @param {undefined|null|string} focusElementId
     */
    set focusElementId(focusElementId) {
        this.#focusElementId = EzString.stringWithTextOrDefault(
            focusElementId,
            null);
    }

    /**
     * @public @readonly @property
     * Gets if the validation response is EzValidationRepsonseStatus.VALID or not.
     * @returns {boolean}
     */
    get valid() {
        return  EzValidationResponseStatus.VALID === this.ezStatus;
    }

    /**
     * @public
     * Gets the Validation status
     * @returns {string}
     * Valid EzValidationResponseStatus enum property value
     * @deprecated
     * Migrate to: EzValidationResponse.status property instead.
     */
    ezGetStatus() {
        return this.ezStatus;
    }

    /**
     * @public
     * Gets the validation error code
     * @returns {number}
     * @deprecated
     * Migrate to the EzValidationResponse.errorCode property instead.
     */
    ezGetErrorCode() {
        return this.errorCode;
    }

    /**
     * @public
     * Gets the validation message
     * @returns {string}
     * @deprecated
     * Migrate to the EzValidationResponse.message property instead.
     */
    ezGetMessage() {
        return this.message;
    }

    /**
     * @public
     * Gets the validation message
     * @returns {string}
     * @deprecated
     * Migrate to the EzValidationResponse.htmlMessage property instead.
     */
    ezGetHtmlMessage() {
        return this.htmlMessage;
    }
}

export {
    // TODO: Remove once all use is migrated to using the official import
    // of import { EzValidationResponseStatus } from '/ezlibrary/enums/Enumerations.js';
    EzValidationResponseStatus,
    EzValidationResponse
};
